<template>
  <v-container fluid class="dashboard-container pa-0">
    <!-- Premium Analytics Header -->
    <v-sheet
      color="white"
      class="pa-8 mb-8 border-bottom shadow-sm"
    >
      <v-row align="center">
        <v-col cols="12" md="6">
          <div class="d-flex align-center">
            <v-avatar size="64" color="primary lighten-5" class="mr-6 shadow-xs rounded-xl border-white overflow-hidden">
              <v-img 
                v-if="parameters.logo_url" 
                :src="parameters.logo_url" 
                contain
                max-width="40"
              ></v-img>
              <v-icon v-else color="primary" large>fas fa-chart-line</v-icon>
            </v-avatar>
            <div>
              <div class="d-flex align-center mb-1">
                <h1 class="text-h4 font-weight-black primary--text tracking-tighter">Panel de Gestión</h1>
                <v-chip x-small color="success" class="ml-4 font-weight-black"></v-chip>
              </div>
              <p class="body-2 grey--text font-weight-medium mb-0">
                Resumen ejecutivo de operaciones, ventas y producción
              </p>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-md-end align-center">
          <div class="d-flex align-center bg-lightgrey px-4 py-2 rounded-xl border shadow-xs mr-4">
            <v-icon x-small color="grey" class="mr-2">fas fa-calendar-alt</v-icon>
            <v-menu
              v-model="menuDate"
              :close-on-content-click="false"
              max-width="290"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <div 
                  v-bind="attrs" 
                  v-on="on" 
                  class="caption font-weight-black primary--text pointer-cursor"
                >
                  {{ computedDateFormattedMomentjs }}
                </div>
              </template>
              <v-date-picker
                v-model="dateRange"
                @change="()=>{menuDate = false; fetch()}"
                range
                color="primary"
              ></v-date-picker>
            </v-menu>
          </div>
          <v-btn
            color="primary"
            class="rounded-xl px-6 font-weight-black text-none shadow-sm"
            @click="fetch"
            :loading="loading"
          >
            <v-icon left size="14">fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-8 pb-12">
      <!-- Loading State -->
      <v-row v-if="loading" dense>
        <v-col v-for="i in 4" :key="i" cols="12" sm="6" md="3">
          <v-skeleton-loader type="card" height="120" class="rounded-xl"></v-skeleton-loader>
        </v-col>
      </v-row>

      <!-- Bespoke Enterprise Metric Cards -->
      <v-row v-else dense class="metrics-grid">
        <!-- Cotizado -->
        <v-col v-if="parameters.dashboard_card_quoted" cols="12" sm="6" md="3">
          <v-card class="bespoke-metric-card h-100" flat>
            <div class="metric-accent blue-accent"></div>
            <v-card-text class="pa-6">
              <div class="metric-label">Cotizado</div>
              <div class="metric-value-container">
                
                <span class="value">{{ total_quoted | moneda }}</span>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="blue" class="mr-1">fas fa-circle</v-icon>
                <span class="status-msg">Proyecciones activas</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Confirmado -->
        <v-col v-if="parameters.dashboard_card_confirmed" cols="12" sm="6" md="3">
          <v-card class="bespoke-metric-card h-100" flat>
            <div class="metric-accent success-accent"></div>
            <v-card-text class="pa-6">
              <div class="metric-label">Confirmado</div>
              <div class="metric-value-container">
                
                <span class="value">{{ total_confirmed | moneda }}</span>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="success" class="mr-1">fas fa-circle</v-icon>
                <span class="status-msg">Ventas cerradas</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Finalizado -->
        <v-col v-if="parameters.dashboard_card_confirmed_finished" cols="12" sm="6" md="3">
          <v-card class="bespoke-metric-card h-100" flat>
            <div class="metric-accent purple-accent"></div>
            <v-card-text class="pa-6">
              <div class="metric-label">Finalizado</div>
              <div class="metric-value-container">
                
                <span class="value">{{ total_confirmed_finished | moneda }}</span>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="purple" class="mr-1">fas fa-circle</v-icon>
                <span class="status-msg">Listo para entrega</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Pendiente Facturar -->
        <v-col v-if="parameters.dashboard_card_confirmed_finished_not_invoiced" cols="12" sm="6" md="3">
          <v-card class="bespoke-metric-card h-100" flat>
            <div class="metric-accent orange-accent"></div>
            <v-card-text class="pa-6">
              <div class="metric-label">Pend. Facturar</div>
              <div class="metric-value-container">
                
                <span class="value text--orange">{{ total_confirmed_finished_not_invoiced | moneda }}</span>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="orange" class="mr-1">fas fa-exclamation-triangle</v-icon>
                <span class="status-msg orange--text">Requiere DTE</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Facturado Neto -->
        <v-col v-if="parameters.dashboard_card_invoiced" cols="12" sm="6" md="3">
          <v-card class="bespoke-metric-card h-100 mt-2" flat>
            <div class="metric-accent teal-accent"></div>
            <v-card-text class="pa-6">
              <div class="metric-label">Facturado Neto</div>
              <div class="metric-value-container">
                
                <span class="value">{{ total_invoiced | moneda }}</span>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="teal" class="mr-1">fas fa-circle</v-icon>
                <span class="status-msg">Ingresos declarados</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Recaudado -->
        <v-col v-if="parameters.dashboard_card_paid" cols="12" sm="6" md="3">
          <v-card class="bespoke-metric-card h-100 mt-2" flat>
            <div class="metric-accent success-accent"></div>
            <v-card-text class="pa-6">
              <div class="metric-label">Recaudado</div>
              <div class="metric-value-container">
                
                <span class="value success--text">{{ total_payments | moneda }}</span>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="success" class="mr-1">fas fa-circle</v-icon>
                <span class="status-msg">Caja real</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Compras Neto -->
        <v-col v-if="parameters.dashboard_card_purchase_subtotal" cols="12" sm="6" md="3">
          <v-card class="bespoke-metric-card h-100 mt-2" flat>
            <div class="metric-accent red-accent"></div>
            <v-card-text class="pa-6">
              <div class="metric-label">Compras Neto</div>
              <div class="metric-value-container">
               
                <span class="value red--text">{{ total_purchase_subtotal | moneda }}</span>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="red" class="mr-1">fas fa-shopping-cart</v-icon>
                <span class="status-msg">Costos operativos</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Compras Pagado -->
        <v-col v-if="parameters.dashboard_card_purchase_paid" cols="12" sm="6" md="3">
          <v-card class="bespoke-metric-card h-100 mt-2" flat>
            <div class="metric-accent secondary-accent"></div>
            <v-card-text class="pa-6">
              <div class="metric-label">Compras Pagado</div>
              <div class="metric-value-container">
                
                <span class="value">{{ total_purchase_payments | moneda }}</span>
              </div>
              <div class="metric-footer">
                <v-icon x-small color="secondary" class="mr-1">fas fa-circle</v-icon>
                <span class="status-msg">Egresos liquidados</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Tarjetas Relbase — Story 5.2 -->
        <template v-if="relbaseEnabled">
          <!-- Skeleton mientras carga -->
          <template v-if="loadingRelbase">
            <v-col v-for="i in 4" :key="`relbase-skeleton-${i}`" cols="12" sm="6" md="3">
              <v-skeleton-loader type="card" height="120" class="rounded-xl mt-2"></v-skeleton-loader>
            </v-col>
          </template>

          <!-- Tarjetas con datos -->
          <template v-else>
            <v-col cols="12" sm="6" md="3">
              <v-card class="bespoke-metric-card h-100 mt-2" flat>
                <div class="metric-accent relbase-accent"></div>
                <v-card-text class="pa-6">
                  <div class="metric-label">Total Facturado (Relbase)</div>
                  <div class="metric-value-container">
                    
                    <span class="value relbase-color">{{ relbaseMetrics.totalFacturado | moneda }}</span>
                  </div>
                  <div class="metric-footer">
                    <v-icon x-small class="mr-1 relbase-color">fas fa-file-invoice-dollar</v-icon>
                    <span class="status-msg">Facturas electrónicas</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card class="bespoke-metric-card h-100 mt-2" flat>
                <div class="metric-accent relbase-accent"></div>
                <v-card-text class="pa-6">
                  <div class="metric-label">DTEs Recientes</div>
                  <div class="metric-value-container">
                    <span class="value relbase-color">{{ relbaseMetrics.dtesRecientes }}</span>
                  </div>
                  <div class="metric-footer">
                    <v-icon x-small class="mr-1 relbase-color">fas fa-receipt</v-icon>
                    <span class="status-msg">Documentos en el período</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card class="bespoke-metric-card h-100 mt-2" flat>
                <div class="metric-accent relbase-accent"></div>
                <v-card-text class="pa-6">
                  <div class="metric-label">Pendientes Facturación</div>
                  <div class="metric-value-container">
                    <span class="value relbase-color">{{ relbaseMetrics.pendientesFacturacion }}</span>
                  </div>
                  <div class="metric-footer">
                    <v-icon x-small class="mr-1 relbase-color">fas fa-clock</v-icon>
                    <span class="status-msg">OTs sin DTE asociado</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card class="bespoke-metric-card h-100 mt-2" flat>
                <div class="metric-accent orange-accent"></div>
                <v-card-text class="pa-6">
                  <div class="metric-label">Finalizado No Facturado (Relbase)</div>
                  <div class="metric-value-container">
                    <span class="currency-symbol orange--text">$</span>
                    <span class="value orange--text">{{ relbaseMetrics.montosPendientesFacturacion | moneda }}</span>
                  </div>
                  <div class="metric-footer">
                    <v-icon x-small color="orange" class="mr-1">fas fa-exclamation-triangle</v-icon>
                    <span class="status-msg orange--text">OTs sin factura Relbase</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </template>
        </template>
      </v-row>

      <!-- Main Operational Analytics Section -->
      <v-row class="mt-8">
        <v-col cols="12">
          <commercial-graph ref="commercialGraph" :external-data="true" />
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import CommercialGraph from '@/components/sales/CommercialGraph.vue'
import { fetchDayGraph } from '@/api/salesNotes'
import { fetchRelbaseMetrics } from '@/api/relbase'
const moment = require('moment')

export default {
  name: 'Home',
  components: {
    CommercialGraph
  },
  data: () => ({
    loading: false,
    loadingRelbase: false,
    menuDate: false,
    dateRange: [moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'), moment().format('YYYY-MM-DD')],
    
    // Data from fetchDayGraph
    total_quoted: 0,
    total_confirmed: 0,
    total_confirmed_finished: 0,
    total_confirmed_finished_not_invoiced: 0,
    total_invoiced: 0,
    total_payments: 0,
    total_purchase_subtotal: 0,
    total_purchase_payments: 0,
    customers: [],
    sellers: [],
    quoted: [],
    confirmed: [],
    invoices: [],
    payments: [],
    relbaseMetrics: {
      totalFacturado: 0,
      dtesRecientes: 0,
      pendientesFacturacion: 0,
      montosPendientesFacturacion: 0,
    }
  }),
  computed: {
    parameters() {
      return this.$store.getters.getCompany || {}
    },
    relbaseEnabled() {
      return !!this.parameters.relbase_integration_enabled
    },
    computedDateFormattedMomentjs () {
      return `${this.dateRange.map(date => moment(date).format('DD MMM YYYY'))[0] || ''} - ${this.dateRange.map(date => moment(date).format('DD MMM YYYY'))[1] || ''}`
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      if (this.relbaseEnabled) this.loadingRelbase = true

      let filterSeller = ''
      if (!this.$can('realAll', 'SalesNote')) {
        const user = this.$store.getters.getUser
        filterSeller = user.id
      }

      let created_before = ''
      let created_after = ''
      let emited_before = ''
      let emited_after = ''

      if (this.dateRange[0]) {
        created_after = moment(this.dateRange[0]).utc().format('yyyy-MM-DD HH:mm')
        emited_after = moment(this.dateRange[0]).format('yyyy-MM-DD')
      }
      if (this.dateRange[1]) {
        created_before = moment(this.dateRange[1]).utc().add(1439, 'minutes').format('yyyy-MM-DD HH:mm')
        emited_before = moment(this.dateRange[1]).format('yyyy-MM-DD')
      }

      const query = `created_after=${created_after}&created_before=${created_before}&emited_after=${emited_after}&emited_before=${emited_before}&customer__seller=${filterSeller}`

      const relbaseParams = {
        start_date: this.dateRange[0] || undefined,
        end_date: this.dateRange[1] || undefined,
      }

      // Lanzar ambos requests en paralelo
      const [mainResult] = await Promise.allSettled([
        fetchDayGraph(query),
        this.relbaseEnabled ? this.loadRelbaseMetrics(relbaseParams) : Promise.resolve(),
      ])

      if (mainResult.status === 'fulfilled') {
        const data = mainResult.value
        this.total_quoted = data.total_quoted
        this.total_confirmed = data.total_confirmed
        this.total_confirmed_finished = data.total_confirmed_finished
        this.total_confirmed_finished_not_invoiced = data.total_confirmed_finished_not_invoiced
        this.total_invoiced = data.total_invoiced
        this.total_payments = data.total_payments
        this.total_purchase_subtotal = data.total_purchase_subtotal
        this.total_purchase_payments = data.total_purchase_payments
        this.customers = data.customers
        this.sellers = data.sellers
        this.quoted = data.quoted
        this.confirmed = data.confirmed
        this.invoices = data.invoices
        this.payments = data.payments

        // Propagate to nested component
        if (this.$refs.commercialGraph) {
          this.$refs.commercialGraph.setData(data, this.dateRange)
        }
      } else {
        console.error('Error fetching dashboard data:', mainResult.reason)
      }

      this.loading = false
    },
    async loadRelbaseMetrics(params = {}) {
      if (!this.relbaseEnabled) return

      try {
        const data = await fetchRelbaseMetrics(params)
        this.relbaseMetrics = {
          totalFacturado: data.total_facturado || 0,
          dtesRecientes: data.dtes_recientes || 0,
          pendientesFacturacion: data.pendientes_facturacion || 0,
          montosPendientesFacturacion: data.monto_pendiente_facturacion || 0,
        }
      } catch (e) {
        console.error('Error cargando métricas Relbase:', e)
      } finally {
        this.loadingRelbase = false
      }
    }
  },
  mounted() {
    this.fetch()
  }
}
</script>

<style scoped>
.dashboard-container { background-color: #f0f2f5; min-height: 100vh; font-family: 'Outfit', sans-serif !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06) !important; }
.shadow-sm { box-shadow: 0 4px 15px rgba(0,0,0,0.03) !important; }
.rounded-xl { border-radius: 12px !important; }

/* Bespoke Metric Cards */
.bespoke-metric-card {
  position: relative;
  background: white !important;
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 16px !important;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  overflow: hidden;
}

.bespoke-metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.06) !important;
  border-color: rgba(0,0,0,0.12) !important;
}

.metric-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.blue-accent { background: #35BDC8; }
.success-accent { background: #4caf50; }
.purple-accent { background: #9c27b0; }
.orange-accent { background: #ff9800; }
.teal-accent { background: #009688; }
.red-accent { background: #f44336; }
.secondary-accent { background: #607d8b; }
.relbase-accent { background: #35BDC8; }
.relbase-color { color: #35BDC8 !important; }

.metric-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

.metric-value-container {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.currency-symbol {
  font-size: 1.25rem;
  font-weight: 500;
  color: #94a3b8;
}

.value {
  font-size: 1.875rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -1px;
}

.metric-footer {
  margin-top: 16px;
  display: flex;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid rgba(0,0,0,0.04);
}

.status-msg {
  font-size: 0.7rem;
  font-weight: 600;
  color: #94a3b8;
}

.pointer-cursor { cursor: pointer; }
</style>
